import { Alert, Box, Button, Center, Code, Group, Stack, Text } from '@mantine/core'
import { Component, type ReactNode } from 'react'
import { LuHouse, LuRefreshCw, LuTriangleAlert } from 'react-icons/lu'

interface Props {
  children: ReactNode
  /** Optional reset key — when this changes, the boundary clears its caught error. */
  resetKey?: string | number
  /** Where to send the user when they click "Go to dashboard". Default `/dashboard`. */
  homeHref?: string
}

interface State {
  error: Error | null
}

/**
 * React error boundary scoped to a route subtree. When a render error is
 * caught, displays a friendly alert + Reload + Go home buttons while the
 * surrounding layout (sidebar, nav, etc.) stays alive.
 *
 * Logs the stack to console + best-effort POST to `/api/admin/logs/app` so
 * the error appears in the SUPER_ADMIN log tail.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null })
    }
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    console.error('[RouteErrorBoundary]', error, info.componentStack)
    fetch('/api/client-errors', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: `${error.name ?? 'Error'}: ${error.message}`,
        detail: info.componentStack ?? error.stack ?? null,
      }),
    }).catch(() => {})
  }

  retry = () => this.setState({ error: null })

  render() {
    const { error } = this.state
    if (!error) return this.props.children
    return (
      <Center mih={400} p="md">
        <Box maw={560}>
          <Alert color="red" icon={<LuTriangleAlert size={20} />} variant="light" radius="md">
            <Stack gap="sm">
              <Text fw={600} size="lg">
                Halaman tidak dapat ditampilkan
              </Text>
              <Text size="sm">
                Terjadi kesalahan saat me-render bagian ini. Bagian lain dari aplikasi tidak terganggu. Coba muat ulang,
                atau kembali ke dashboard.
              </Text>
              <Code block c="red" style={{ maxWidth: '100%', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                {error.message || error.name || 'Unknown error'}
              </Code>
              <Group gap="xs">
                <Button leftSection={<LuRefreshCw size={16} />} onClick={this.retry} color="red" variant="light">
                  Coba lagi
                </Button>
                <Button
                  leftSection={<LuHouse size={16} />}
                  variant="default"
                  component="a"
                  href={this.props.homeHref ?? '/dashboard'}
                >
                  Ke Dashboard
                </Button>
              </Group>
            </Stack>
          </Alert>
        </Box>
      </Center>
    )
  }
}
